import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useCartStore } from '@/stores/cartStore'
import { Button } from '@/components/ui/Button'
import { Image } from '@/components/ui/Image'
import { formatPrice, cn } from '@/lib/utils'
import { Check, X, ShoppingBag } from 'lucide-react'

interface AddToBagConfirmationProps {
  open: boolean
  onClose: () => void
  perfumeName: string
  sizeLabel: string
  price: number
  image: string
}

export function AddToBagConfirmation({
  open,
  onClose,
  perfumeName,
  sizeLabel,
  price,
  image,
}: AddToBagConfirmationProps) {
  const { toggleCart, items } = useCartStore()
  const cartCount = items.reduce((sum, i) => sum + i.quantity, 0)

  const viewBag = () => {
    onClose()
    toggleCart(true)
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/20 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', ease: [0.16, 1, 0.3, 1], duration: 0.35 }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-paper shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-ink/10 px-6 py-5">
              <div className="flex items-center gap-2">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-wd-orange text-white">
                  <Check className="h-4 w-4" />
                </span>
                <span className="font-display text-lg normal-case text-carbon">Added to bag</span>
              </div>
              <button onClick={onClose} aria-label="Close" className="text-carbon/50 hover:text-wd-orange">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="flex gap-4 border-b border-ink/10 pb-6">
                <Image
                  src={image}
                  alt={perfumeName}
                  aspectRatio="3/4"
                  containerClassName="h-32 w-24 shrink-0 bg-oyster"
                  className="h-full w-full object-cover"
                />
                <div className="flex flex-col justify-between">
                  <div>
                    <p className="font-display text-lg normal-case text-carbon">{perfumeName.toLowerCase()}</p>
                    <p className="text-sm text-carbon/60">{sizeLabel}</p>
                  </div>
                  <p className="text-lg text-carbon">{formatPrice(price)}</p>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-carbon/60">Bag total ({cartCount} {cartCount === 1 ? 'item' : 'items'})</span>
                  <span className="font-medium text-carbon">
                    {formatPrice(
                      items.reduce((sum, i) => sum + i.price * i.quantity, 0)
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-ink/10 px-6 py-6">
              <Button onClick={viewBag} className="w-full">
                <ShoppingBag className={cn('mr-2 h-4 w-4')} /> View bag
              </Button>
              <Link
                to="/shop"
                onClick={onClose}
                className="mt-3 block w-full border border-ink/10 py-3 text-center text-xs font-medium uppercase tracking-[0.12em] text-carbon transition-colors hover:bg-oyster"
              >
                Continue shopping
              </Link>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
